import {
  Conversation,
  ConversationContext,
  ConversationMessage,
  ConversationMessageSentimentFeedback,
} from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { AsyncAction, SyncAction } from './common.actionTypes';
import { ConversationDocument, ConversationNewMessage } from '../reducers/aiAssistant.reducers';
import {
  AiGeneratedArtifacts,
  AiGeneratedDocument,
  GeneratedPodcastArtifact,
  AttachmentsRateLimit,
  ConversationAttachment,
  ImagesRateLimit,
  PromptsRateLimit,
  StreamAIDocumentChunk,
  StreamAIDocumentMetadata,
  StreamAIDocumentTitle,
  StreamAIEventMessageMetadata,
  StreamAIEventMetadata,
} from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import { SearchResponseData } from '@klab-berlin/api-sdk/lib/types/responses/Common';
import { ConversationListItem } from '@klab-berlin/api-sdk/lib/types/responses/Conversation';
import { GetConversationsQuery } from '@klab-berlin/api-sdk/lib/types/requests/Conversation';
import { GenerateSuggestions } from '@klab-berlin/api-sdk/lib/types/requests/Ai';

export enum STREAM_CONVERSATION_RESPONSE {
  START = 'STREAM_CONVERSATION_RESPONSE_START',
  SUCCESS = 'STREAM_CONVERSATION_RESPONSE_SUCCESS',
  ERROR = 'STREAM_CONVERSATION_RESPONSE_ERROR',
}

type CreateNewConversationType = 'CREATE_NEW_CONVERSATION';
export const CREATE_NEW_CONVERSATION: CreateNewConversationType = 'CREATE_NEW_CONVERSATION';

type SetCurrentConversationIdType = 'SET_CURRENT_CONVERSATION_ID';
export const SET_CURRENT_CONVERSATION_ID: SetCurrentConversationIdType = 'SET_CURRENT_CONVERSATION_ID';

type StreamEventMessageChunkType = 'STREAM_EVENT_MESSAGE_CHUNK';
export const STREAM_EVENT_MESSAGE_CHUNK: StreamEventMessageChunkType = 'STREAM_EVENT_MESSAGE_CHUNK';

type StreamEventMetadataType = 'STREAM_EVENT_METADATA';
export const STREAM_EVENT_METADATA: StreamEventMetadataType = 'STREAM_EVENT_METADATA';

type StreamEventMessageMetadataType = 'STREAM_EVENT_MESSAGE_METADATA';
export const STREAM_EVENT_MESSAGE_METADATA: StreamEventMessageMetadataType = 'STREAM_EVENT_MESSAGE_METADATA';

type StreamEventDocumentStartType = 'STREAM_EVENT_DOCUMENT_START';
export const STREAM_EVENT_DOCUMENT_START: StreamEventDocumentStartType = 'STREAM_EVENT_DOCUMENT_START';

type StreamEventDocumentDoneType = 'STREAM_EVENT_DOCUMENT_DONE';
export const STREAM_EVENT_DOCUMENT_DONE: StreamEventDocumentDoneType = 'STREAM_EVENT_DOCUMENT_DONE';

type StreamEventDocumentChunkType = 'STREAM_EVENT_DOCUMENT_CHUNK';
export const STREAM_EVENT_DOCUMENT_CHUNK: StreamEventDocumentChunkType = 'STREAM_EVENT_DOCUMENT_CHUNK';

type StreamEventDocumentMetadataType = 'STREAM_EVENT_DOCUMENT_METADATA';
export const STREAM_EVENT_DOCUMENT_METADATA: StreamEventDocumentMetadataType = 'STREAM_EVENT_DOCUMENT_METADATA';

type StreamEventDocumentTitleType = 'STREAM_EVENT_DOCUMENT_TITLE';
export const STREAM_EVENT_DOCUMENT_TITLE: StreamEventDocumentTitleType = 'STREAM_EVENT_DOCUMENT_TITLE';

type StreamEventDoneType = 'STREAM_EVENT_DONE';
export const STREAM_EVENT_DONE: StreamEventDoneType = 'STREAM_EVENT_DONE';

type StreamEventTitleUpdateType = 'STREAM_EVENT_TITLE_UPDATE';
export const STREAM_EVENT_TITLE_UPDATE: StreamEventTitleUpdateType = 'STREAM_EVENT_TITLE_UPDATE';

type StreamErrorType = 'STREAM_ERROR';
export const STREAM_ERROR: StreamErrorType = 'STREAM_ERROR';

type AddNewMessageType = 'ADD_NEW_MESSAGE';
export const ADD_NEW_MESSAGE: AddNewMessageType = 'ADD_NEW_MESSAGE';

type RemoveLastAssistantResponseType = 'REMOVE_LAST_ASSISTANT_RESPONSE';
export const REMOVE_LAST_ASSISTANT_RESPONSE: RemoveLastAssistantResponseType = 'REMOVE_LAST_ASSISTANT_RESPONSE';

type RemoveTemporaryAttachmentType = 'REMOVE_TEMPORARY_ATTACHMENT';
export const REMOVE_TEMPORARY_ATTACHMENT: RemoveTemporaryAttachmentType = 'REMOVE_TEMPORARY_ATTACHMENT';

type ClearTemporaryAttachmentsType = 'CLEAR_TEMPORARY_ATTACHMENTS';
export const CLEAR_TEMPORARY_ATTACHMENTS: ClearTemporaryAttachmentsType = 'CLEAR_TEMPORARY_ATTACHMENTS';

type SaveContextAttachmentsType = 'SAVE_CONTEXT_ATTACHMENTS';
export const SAVE_CONTEXT_ATTACHMENTS: SaveContextAttachmentsType = 'SAVE_CONTEXT_ATTACHMENTS';

type RestoreContextAttachmentsType = 'RESTORE_CONTEXT_ATTACHMENTS';
export const RESTORE_CONTEXT_ATTACHMENTS: RestoreContextAttachmentsType = 'RESTORE_CONTEXT_ATTACHMENTS';

type ClearContextAttachmentsType = 'CLEAR_CONTEXT_ATTACHMENTS';
export const CLEAR_CONTEXT_ATTACHMENTS: ClearContextAttachmentsType = 'CLEAR_CONTEXT_ATTACHMENTS';

type ClearTemporaryConversationType = 'CLEAR_TEMPORARY_CONVERSATION';
export const CLEAR_TEMPORARY_CONVERSATION: ClearTemporaryConversationType = 'CLEAR_TEMPORARY_CONVERSATION';

type ClearDocumentStreamType = 'CLEAR_DOCUMENT_STREAM';
export const CLEAR_DOCUMENT_STREAM: ClearDocumentStreamType = 'CLEAR_DOCUMENT_STREAM';

type StreamEventToolUseStartType = 'STREAM_EVENT_TOOL_USE_START';
export const STREAM_EVENT_TOOL_USE_START: StreamEventToolUseStartType = 'STREAM_EVENT_TOOL_USE_START';

type StreamEventToolUseEndType = 'STREAM_EVENT_TOOL_USE_END';
export const STREAM_EVENT_TOOL_USE_END: StreamEventToolUseEndType = 'STREAM_EVENT_TOOL_USE_END';

type ClearLastGeneratedImageUrlType = 'CLEAR_LAST_GENERATED_IMAGE_URL';
export const CLEAR_LAST_GENERATED_IMAGE_URL: ClearLastGeneratedImageUrlType = 'CLEAR_LAST_GENERATED_IMAGE_URL';

type ClearLastGeneratedArtifactIdType = 'CLEAR_LAST_GENERATED_ARTIFACT_ID';
export const CLEAR_LAST_GENERATED_ARTIFACT_ID: ClearLastGeneratedArtifactIdType = 'CLEAR_LAST_GENERATED_ARTIFACT_ID';

export enum SAVE_MESSAGE_SENTIMENT {
  START = 'SAVE_MESSAGE_SENTIMENT_START',
  SUCCESS = 'SAVE_MESSAGE_SENTIMENT_SUCCESS',
  ERROR = 'SAVE_MESSAGE_SENTIMENT_ERROR',
}

export enum GET_CONVERSATIONS {
  START = 'GET_CONVERSATIONS_START',
  SUCCESS = 'GET_CONVERSATIONS_SUCCESS',
  ERROR = 'GET_CONVERSATIONS_ERROR',
}

export enum DELETE_CONVERSATION {
  START = 'DELETE_CONVERSATION_START',
  SUCCESS = 'DELETE_CONVERSATION_SUCCESS',
  ERROR = 'DELETE_CONVERSATION_ERROR'
}

export enum GET_CONVERSATION {
  START = 'GET_CONVERSATION_START',
  SUCCESS = 'GET_CONVERSATION_SUCCESS',
  ERROR = 'GET_CONVERSATION_ERROR',
}

export enum GENERATE_AI_SUGGESTIONS {
  START = 'GENERATE_AI_SUGGESTIONS_START',
  SUCCESS = 'GENERATE_AI_SUGGESTIONS_SUCCESS',
  ERROR = 'GENERATE_AI_SUGGESTIONS_ERROR',
}

export enum UPLOAD_ATTACHMENT {
  START = 'UPLOAD_ATTACHMENT_START',
  SUCCESS = 'UPLOAD_ATTACHMENT_SUCCESS',
  ERROR = 'UPLOAD_ATTACHMENT_ERROR',
}

export enum UPLOAD_IMAGE {
  START = 'UPLOAD_IMAGE_START',
  SUCCESS = 'UPLOAD_IMAGE_SUCCESS',
  ERROR = 'UPLOAD_IMAGE_ERROR',
}

export enum GET_ATTACHMENT {
  START = 'GET_ATTACHMENT_START',
  SUCCESS = 'GET_ATTACHMENT_SUCCESS',
  ERROR = 'GET_ATTACHMENT_ERROR',
}

export enum GET_ATTACHMENTS_REMAINING_LIMIT {
  START = 'GET_ATTACHMENTS_REMAINING_LIMIT_START',
  SUCCESS = 'GET_ATTACHMENTS_REMAINING_LIMIT_SUCCESS',
  ERROR = 'GET_ATTACHMENTS_REMAINING_LIMIT_ERROR',
}

export enum GET_GENERATED_IMAGES_REMAINING_LIMIT {
  START = 'GET_GENERATED_IMAGES_REMAINING_LIMIT_START',
  SUCCESS = 'GET_GENERATED_IMAGES_REMAINING_LIMIT_SUCCESS',
  ERROR = 'GET_GENERATED_IMAGES_REMAINING_LIMIT_ERROR',
}

export enum GET_PROMPTS_REMAINING_LIMIT {
  START = 'GET_PROMPTS_REMAINING_LIMIT_START',
  SUCCESS = 'GET_PROMPTS_REMAINING_LIMIT_SUCCESS',
  ERROR = 'GET_PROMPTS_REMAINING_LIMIT_ERROR',
}

export enum GET_GENERATED_DOCUMENT {
  START = 'GET_GENERATED_DOCUMENT_START',
  SUCCESS = 'GET_GENERATED_DOCUMENT_SUCCESS',
  ERROR = 'GET_GENERATED_DOCUMENT_ERROR',
}

export enum GET_GENERATED_ARTIFACTS {
  START = 'GET_GENERATED_ARTIFACTS_START',
  SUCCESS = 'GET_GENERATED_ARTIFACTS_SUCCESS',
  ERROR = 'GET_GENERATED_ARTIFACTS_ERROR',
}

export enum UPDATE_GENERATED_DOCUMENT {
  START = 'UPDATE_GENERATED_DOCUMENT_START',
  SUCCESS = 'UPDATE_GENERATED_DOCUMENT_SUCCESS',
  ERROR = 'UPDATE_GENERATED_DOCUMENT_ERROR',
}

export enum SYNC_GENERATED_DOCUMENT {
  START = 'SYNC_GENERATED_DOCUMENT_START',
  SUCCESS = 'SYNC_GENERATED_DOCUMENT_SUCCESS',
  ERROR = 'SYNC_GENERATED_DOCUMENT_ERROR',
}

export enum UPDATE_GENERATED_PODCAST {
  START = 'UPDATE_GENERATED_PODCAST_START',
  SUCCESS = 'UPDATE_GENERATED_PODCAST_SUCCESS',
  ERROR = 'UPDATE_GENERATED_PODCAST_ERROR',
}

type PatchGeneratedArtifactType = 'PATCH_GENERATED_ARTIFACT';
export const PATCH_GENERATED_ARTIFACT: PatchGeneratedArtifactType = 'PATCH_GENERATED_ARTIFACT';

type SetActiveDocumentIdType = 'SET_ACTIVE_DOCUMENT_ID';
export const SET_ACTIVE_DOCUMENT_ID: SetActiveDocumentIdType = 'SET_ACTIVE_DOCUMENT_ID';

type ClearActiveDocumentType = 'CLEAR_ACTIVE_DOCUMENT';
export const CLEAR_ACTIVE_DOCUMENT: ClearActiveDocumentType = 'CLEAR_ACTIVE_DOCUMENT';

export type StreamConversationResponse = AsyncAction<
  typeof STREAM_CONVERSATION_RESPONSE.START,
  typeof STREAM_CONVERSATION_RESPONSE.SUCCESS,
  typeof STREAM_CONVERSATION_RESPONSE.ERROR,
  undefined,
  { conversationId: string }
>;

export type CreateNewConversationAction = SyncAction<
  typeof CREATE_NEW_CONVERSATION,
  undefined,
  {
    id: string
    message: ConversationNewMessage
    context?: ConversationContext
  }
>;

export type SetCurrentConversationIdAction = SyncAction<
  typeof SET_CURRENT_CONVERSATION_ID,
  undefined,
  { conversationId: string | null }
>;

export type StreamEventMessageChunkAction = SyncAction<
  typeof STREAM_EVENT_MESSAGE_CHUNK,
  undefined,
  { conversationId: string, message: ConversationNewMessage }
>;

export type StreamEventMetadataAction = SyncAction<
  typeof STREAM_EVENT_METADATA,
  undefined,
  { metadata: StreamAIEventMetadata }
>;

export type StreamEventTitleUpdateAction = SyncAction<
  typeof STREAM_EVENT_TITLE_UPDATE,
  undefined,
  { conversationId: string; title: string }
>;

export type StreamEventMessageMetadataAction = SyncAction<
  typeof STREAM_EVENT_MESSAGE_METADATA,
  undefined,
  { conversationId: string, messageId: string, metadata: StreamAIEventMessageMetadata }
>;

export type StreamEventDocumentChunkAction = SyncAction<
  typeof STREAM_EVENT_DOCUMENT_CHUNK,
  undefined,
  { document: StreamAIDocumentChunk }
>;

export type StreamEventDocumentMetadataAction = SyncAction<
  typeof STREAM_EVENT_DOCUMENT_METADATA,
  undefined,
  { metadata: StreamAIDocumentMetadata }
>;

export type StreamEventDocumentStartAction = SyncAction<
  typeof STREAM_EVENT_DOCUMENT_START,
  undefined
>;

export type StreamEventDocumentDoneAction = SyncAction<
  typeof STREAM_EVENT_DOCUMENT_DONE,
  undefined
>;

export type StreamEventDocumentTitleAction = SyncAction<
  typeof STREAM_EVENT_DOCUMENT_TITLE,
  undefined,
  { title: StreamAIDocumentTitle }
>;

export type StreamEventDoneAction = SyncAction<
  typeof STREAM_EVENT_DONE,
  undefined,
  {
    conversationId: string;
    message: ConversationMessage;
  }
>;

export type StreamErrorAction = SyncAction<
  typeof STREAM_ERROR,
  undefined,
  {
    conversationId: string,
    errorMessage: ConversationNewMessage | null,  // null for silent aborts
    rateLimit?: { messages?: { expiresAt: Date }, attachments?: { expiresAt: Date } },
    streamMessage?: ConversationMessage,
    streamDocument?: ConversationDocument | null
  }
>;

export type AddNewMessageAction = SyncAction<
  typeof ADD_NEW_MESSAGE,
  undefined,
  { conversationId: string, message: ConversationNewMessage }
>;

export type RemoveLastAssistantResponseAction = SyncAction<
  typeof REMOVE_LAST_ASSISTANT_RESPONSE,
  undefined,
  { conversationId: string }
>;

export type RemoveTemporaryAttachmentAction = SyncAction<
  typeof REMOVE_TEMPORARY_ATTACHMENT,
  undefined,
  { attachmentHashName: string }
>;

export type ClearTemporaryAttachmentsAction = SyncAction<
  typeof CLEAR_TEMPORARY_ATTACHMENTS,
  undefined,
  undefined
>;

export type SaveContextAttachmentsAction = SyncAction<
  typeof SAVE_CONTEXT_ATTACHMENTS,
  undefined,
  { contextKey: string; attachments: string[] }
>;

export type RestoreContextAttachmentsAction = SyncAction<
  typeof RESTORE_CONTEXT_ATTACHMENTS,
  undefined,
  { contextKey: string }
>;

export type ClearContextAttachmentsAction = SyncAction<
  typeof CLEAR_CONTEXT_ATTACHMENTS,
  undefined,
  { contextKey: string }
>;

export type ClearTemporaryConversationAction = SyncAction<
  typeof CLEAR_TEMPORARY_CONVERSATION,
  undefined,
  undefined
>;

export type ClearDocumentStreamAction = SyncAction<
  typeof CLEAR_DOCUMENT_STREAM,
  undefined,
  undefined
>;

export type StreamEventToolUseStartAction = SyncAction<
  typeof STREAM_EVENT_TOOL_USE_START,
  undefined,
  { toolName: string }
>;

export type StreamEventToolUseEndAction = SyncAction<
  typeof STREAM_EVENT_TOOL_USE_END,
  undefined,
  {
    toolName: string;
    remainingLimit?: number;
    availableAgainAt?: number | null;
    presignedUrl?: string;
    imageId?: string;
  }
>;

export type ClearLastGeneratedImageUrlAction = SyncAction<
  typeof CLEAR_LAST_GENERATED_IMAGE_URL,
  undefined,
  undefined
>;

export type ClearLastGeneratedArtifactIdAction = SyncAction<
  typeof CLEAR_LAST_GENERATED_ARTIFACT_ID,
  undefined,
  undefined
>;

export type SyncGeneratedDocumentAction = AsyncAction<
  typeof SYNC_GENERATED_DOCUMENT.START,
  typeof SYNC_GENERATED_DOCUMENT.SUCCESS,
  typeof SYNC_GENERATED_DOCUMENT.ERROR,
  undefined,
  { documentId: string }
>;

export type SaveMessageSentimentAction = AsyncAction<
  typeof SAVE_MESSAGE_SENTIMENT.START,
  typeof SAVE_MESSAGE_SENTIMENT.SUCCESS,
  typeof SAVE_MESSAGE_SENTIMENT.ERROR,
  undefined,
  { conversationId: string, messageId: string, sentimentFeedback: ConversationMessageSentimentFeedback }
>;

export type GetConversationsAction = AsyncAction<
  typeof GET_CONVERSATIONS.START,
  typeof GET_CONVERSATIONS.SUCCESS,
  typeof GET_CONVERSATIONS.ERROR,
  SearchResponseData<ConversationListItem>,
  { query: GetConversationsQuery }
>;

export type DeleteConversationAction = AsyncAction<
  typeof DELETE_CONVERSATION.START,
  typeof DELETE_CONVERSATION.SUCCESS,
  typeof DELETE_CONVERSATION.ERROR,
  undefined,
  { conversationId: string }
>;

export type GetConversationAction = AsyncAction<
  typeof GET_CONVERSATION.START,
  typeof GET_CONVERSATION.SUCCESS,
  typeof GET_CONVERSATION.ERROR,
  Conversation,
  { conversationId: string }
>;

export type GenerateAISuggestionsAction = AsyncAction<
  typeof GENERATE_AI_SUGGESTIONS.START,
  typeof GENERATE_AI_SUGGESTIONS.SUCCESS,
  typeof GENERATE_AI_SUGGESTIONS.ERROR,
  string[],
  { requestId: string; payload: GenerateSuggestions }
>;

export type UploadAttachmentAction = AsyncAction<
  typeof UPLOAD_ATTACHMENT.START,
  typeof UPLOAD_ATTACHMENT.SUCCESS,
  typeof UPLOAD_ATTACHMENT.ERROR,
  ConversationAttachment,
  { temporaryAttachmentHashName: string }
>;

export type UploadImageAction = AsyncAction<
  typeof UPLOAD_IMAGE.START,
  typeof UPLOAD_IMAGE.SUCCESS,
  typeof UPLOAD_IMAGE.ERROR,
  { displayUrl: string },
  undefined
>;

export type GetAttachmentAction = AsyncAction<
  typeof GET_ATTACHMENT.START,
  typeof GET_ATTACHMENT.SUCCESS,
  typeof GET_ATTACHMENT.ERROR,
  ConversationAttachment,
  { attachmentHashName: string }
>;

export type GetAttachmentsRemainingLimitAction = AsyncAction<
  typeof GET_ATTACHMENTS_REMAINING_LIMIT.START,
  typeof GET_ATTACHMENTS_REMAINING_LIMIT.SUCCESS,
  typeof GET_ATTACHMENTS_REMAINING_LIMIT.ERROR,
  AttachmentsRateLimit,
  undefined
>;

export type GetGeneratedImagesRemainingLimitAction = AsyncAction<
  typeof GET_GENERATED_IMAGES_REMAINING_LIMIT.START,
  typeof GET_GENERATED_IMAGES_REMAINING_LIMIT.SUCCESS,
  typeof GET_GENERATED_IMAGES_REMAINING_LIMIT.ERROR,
  ImagesRateLimit,
  undefined
>;

export type GetPromptsRemainingLimitAction = AsyncAction<
  typeof GET_PROMPTS_REMAINING_LIMIT.START,
  typeof GET_PROMPTS_REMAINING_LIMIT.SUCCESS,
  typeof GET_PROMPTS_REMAINING_LIMIT.ERROR,
  PromptsRateLimit,
  undefined
>;

export type GetGeneratedDocumentAction = AsyncAction<
  typeof GET_GENERATED_DOCUMENT.START,
  typeof GET_GENERATED_DOCUMENT.SUCCESS,
  typeof GET_GENERATED_DOCUMENT.ERROR,
  AiGeneratedDocument,
  { documentId: string }
>;

export type GetGeneratedArtifactsAction = AsyncAction<
  typeof GET_GENERATED_ARTIFACTS.START,
  typeof GET_GENERATED_ARTIFACTS.SUCCESS,
  typeof GET_GENERATED_ARTIFACTS.ERROR,
  AiGeneratedArtifacts,
  { batch: number }
>;

export type UpdateGeneratedDocumentAction = AsyncAction<
  typeof UPDATE_GENERATED_DOCUMENT.START,
  typeof UPDATE_GENERATED_DOCUMENT.SUCCESS,
  typeof UPDATE_GENERATED_DOCUMENT.ERROR,
  undefined,
  { documentId: string, payload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>> }
>;

export type UpdateGeneratedPodcastAction = AsyncAction<
  typeof UPDATE_GENERATED_PODCAST.START,
  typeof UPDATE_GENERATED_PODCAST.SUCCESS,
  typeof UPDATE_GENERATED_PODCAST.ERROR,
  undefined,
  { podcastId: string, title: string }
>;

export type PatchGeneratedArtifactAction = SyncAction<
  typeof PATCH_GENERATED_ARTIFACT,
  undefined,
  {
    artifactId: string,
    changes: Pick<GeneratedPodcastArtifact, 'status' | 'transcript'>,
  }
>;

export type SetActiveDocumentIdAction = SyncAction<
  typeof SET_ACTIVE_DOCUMENT_ID,
  undefined,
  { documentId: string }
>;

export type ClearActiveDocumentAction = SyncAction<
  typeof CLEAR_ACTIVE_DOCUMENT,
  undefined,
  undefined
>;

export type AIAssistantActionTypes =
  | CreateNewConversationAction
  | StreamConversationResponse
  | SetCurrentConversationIdAction
  | StreamEventMessageChunkAction
  | StreamEventMetadataAction
  | StreamEventTitleUpdateAction
  | StreamEventMessageMetadataAction
  | StreamEventDoneAction
  | StreamErrorAction
  | AddNewMessageAction
  | RemoveLastAssistantResponseAction
  | SaveMessageSentimentAction
  | GetConversationsAction
  | DeleteConversationAction
  | GetConversationAction
  | GenerateAISuggestionsAction
  | UploadAttachmentAction
  | UploadImageAction
  | GetAttachmentAction
  | GetAttachmentsRemainingLimitAction
  | GetGeneratedImagesRemainingLimitAction
  | GetPromptsRemainingLimitAction
  | ClearLastGeneratedImageUrlAction
  | ClearLastGeneratedArtifactIdAction
  | RemoveTemporaryAttachmentAction
  | ClearTemporaryAttachmentsAction
  | SaveContextAttachmentsAction
  | RestoreContextAttachmentsAction
  | ClearContextAttachmentsAction
  | ClearTemporaryConversationAction
  | ClearDocumentStreamAction
  | StreamEventDocumentChunkAction
  | StreamEventDocumentMetadataAction
  | StreamEventDocumentTitleAction
  | StreamEventDocumentStartAction
  | StreamEventDocumentDoneAction
  | StreamEventToolUseStartAction
  | StreamEventToolUseEndAction
  | GetGeneratedDocumentAction
  | GetGeneratedArtifactsAction
  | PatchGeneratedArtifactAction
  | UpdateGeneratedDocumentAction
  | UpdateGeneratedPodcastAction
  | SetActiveDocumentIdAction
  | ClearActiveDocumentAction
  | SyncGeneratedDocumentAction;
